import React, { useEffect, useMemo } from 'react';
import './ProvisioningStatus.css';

const ProvisioningStatus = ({
    data,
    error,
    progress,
    elapsed,
    amplifyData,
    amplifyError,
    amplifyProgress,
    onCompleted
}) => {
    // Merge MongoDB AmplifyApps with real-time amplifyData
    const mergedAmplifyApps = useMemo(() => {
        if (!data?.AmplifyApps) return [];
        if (!amplifyData || amplifyData.length === 0) return data.AmplifyApps;

        // Create a map of real-time data by AppId
        const realtimeMap = {};
        amplifyData.forEach(app => {
            realtimeMap[app.AppId] = app;
        });

        // Merge: Use MongoDB as base, override with real-time data if available
        return data.AmplifyApps.map(mongoApp => {
            const realtimeApp = realtimeMap[mongoApp.AppId];

            if (!realtimeApp) {
                // No real-time data yet, use MongoDB data
                return mongoApp;
            }

            // Merge branches - use real-time Status if available
            const mergedBranches = (mongoApp.ConnectedBranches || []).map(mongoBranch => {
                const realtimeBranch = (realtimeApp.Branches || []).find(
                    rb => rb.BranchName === mongoBranch.BranchName
                );

                return {
                    ...mongoBranch,
                    // Override with real-time status if available
                    Status: realtimeBranch?.Status || mongoBranch.Status,
                    JobId: realtimeBranch?.JobId || mongoBranch.JobId,
                    LatestJob: realtimeBranch?.LatestJob || mongoBranch.LatestJob
                };
            });

            return {
                ...mongoApp,
                Branches: mergedBranches, // Use "Branches" for consistent naming
                ConnectedBranches: mergedBranches, // Keep both for compatibility
                Error: realtimeApp.Error // Show errors from real-time API
            };
        });
    }, [data?.AmplifyApps, amplifyData]);

    const isBackendComplete = progress === 100;
    const isAmplifyComplete = amplifyProgress === 100;
    const isFullyComplete = isBackendComplete && data?.HasFrontendData && isAmplifyComplete;

    useEffect(() => {
        if (isFullyComplete && typeof onCompleted === 'function') {
            onCompleted();
        };
    }, [isFullyComplete, onCompleted]);

    if (!data && !error) {
        return null;
    }

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getStatusColor = (status) => {
        if (!status) return '#6c757d';

        const upperStatus = status.toUpperCase();
        if (upperStatus === 'SUCCEEDED' || upperStatus === 'SUCCEED') return '#28a745';
        if (upperStatus === 'FAILED') return '#dc3545';
        if (upperStatus === 'RUNNING' || upperStatus === 'IN_PROGRESS' || upperStatus === 'DEPLOYING') return '#ffc107';
        if (upperStatus === 'PENDING' || upperStatus === 'QUEUED') return '#6c757d';
        return '#17a2b8';
    };

    const getProgressColor = () => {
        if (progress === 100) return '#28a745';
        if (progress >= 50) return '#ffc107';
        return '#17a2b8';
    };

    // Helper to get branches - handles both ConnectedBranches and Branches
    const getBranches = (app) => {
        return app.Branches || app.ConnectedBranches || [];
    };

    return (
        <div className="provisioning-status-container">
            {/* Header with overall progress */}
            <div className="status-header">
                <h2 className="status-title">
                    <span className="status-icon">🚀</span>
                    Infrastructure Deployment Status
                </h2>
                <div className="elapsed-time">
                    <span className="clock-icon">⏱️</span>
                    {formatTime(elapsed)}
                </div>
            </div>

            {/* Overall Success Banner */}
            {isFullyComplete && (
                <div className="success-banner">
                    <span className="success-icon">🎉</span>
                    <span>Complete infrastructure deployed successfully!</span>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="error-banner">
                    <span className="error-icon">⚠️</span>
                    <span>{"Something went wrong!. Please contact support if the issue persists."}</span>
                </div>
            )}

            {/* Backend Section (Terraform + Microservices) */}
            {data && (
                <div className="backend-section">
                    <div className="section-header">
                        <h3 className="section-title">Backend Infrastructure</h3>
                        <div className="progress-section">
                            <div className="progress-bar-container">
                                <div
                                    className="progress-bar-fill"
                                    style={{
                                        width: `${progress}%`,
                                        backgroundColor: getProgressColor()
                                    }}
                                />
                            </div>
                            <span className="progress-text">{progress}%</span>
                        </div>
                    </div>

                    {/* Terraform Pipeline */}
                    <div className="pipeline-row terraform-row">
                        <div className="pipeline-info">
                            <span className="pipeline-icon">🏗️</span>
                            <span className="pipeline-name">{data.PipelineName || 'Terraform Pipeline'}</span>
                            {data.BranchName && (
                                <span className="pipeline-branch">({data.BranchName})</span>
                            )}
                        </div>
                        <div
                            className="pipeline-status"
                            style={{ backgroundColor: getStatusColor(data.State) }}
                        >
                            {data.State}
                        </div>
                    </div>

                    {/* Child Pipelines (Microservices) */}
                    {data.ChildPipelines && data.ChildPipelines.length > 0 && (
                        <div className="child-pipelines-section">
                            <h4 className="subsection-title">Microservices ({data.ChildPipelines.length})</h4>
                            <div className="pipelines-scrollable">
                                {data.ChildPipelines.map((child, index) => (
                                    <div key={index} className="pipeline-row">
                                        <div className="pipeline-info">
                                            <span className="pipeline-icon">⚙️</span>
                                            <span className="pipeline-name">{child.PipelineName}</span>
                                        </div>
                                        <div
                                            className="pipeline-status"
                                            style={{ backgroundColor: getStatusColor(child.State) }}
                                        >
                                            {child.State}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Frontend Section (Amplify) - Use MERGED data */}
            {data && data.HasFrontendData && mergedAmplifyApps.length > 0 && (
                <div className="frontend-section">
                    <div className="section-header">
                        <h3 className="section-title">Frontend Deployment (Amplify)</h3>
                        <div className="progress-section">
                            <div className="progress-bar-container">
                                <div
                                    className="progress-bar-fill"
                                    style={{
                                        width: `${amplifyProgress}%`,
                                        backgroundColor: amplifyProgress === 100 ? '#28a745' : '#ffc107'
                                    }}
                                />
                            </div>
                            <span className="progress-text">{amplifyProgress}%</span>
                        </div>
                    </div>

                    {/* Branch Info */}
                    {(data.EmployerBranch || data.CandidateBranch) && (
                        <div className="branch-details">
                            {data.EmployerBranch && (
                                <div className="detail-row">
                                    <span className="detail-label">🏢 Employer Branch:</span>
                                    <span className="detail-value">{data.EmployerBranch}</span>
                                </div>
                            )}
                            {data.CandidateBranch && (
                                <div className="detail-row">
                                    <span className="detail-label">👥 Candidate Branch:</span>
                                    <span className="detail-value">{data.CandidateBranch}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Amplify Apps with MERGED data */}
                    <div className="amplify-apps-container">
                        {mergedAmplifyApps.map((app, appIndex) => {
                            const branches = getBranches(app);
                            return (
                                <div key={appIndex} className="amplify-app-card">
                                    <div className="app-header">
                                        <div className="app-name">
                                            <span className="app-icon">📱</span>
                                            {app.AppName}
                                        </div>
                                        <div className="app-id">ID: {app.AppId}</div>
                                    </div>

                                    {app.DefaultDomain && (
                                        <div className="app-domain">
                                            🔗 <a 
                                                href={`https://${app.DefaultDomain}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {app.DefaultDomain}
                                            </a>
                                        </div>
                                    )}

                                    {/* Error from AmplifyStatusFetcher */}
                                    {app.Error && (
                                        <div className="app-error">
                                            <span className="error-icon">⚠️</span>
                                            {"Something went wrong!. Please contact support if the issue persists."}
                                        </div>
                                    )}

                                    {/* Branches with merged real-time status */}
                                    {branches.length > 0 ? (
                                        <div className="branches-container">
                                            {branches.map((branch, branchIndex) => (
                                                <div key={branchIndex} className="branch-row">
                                                    <div className="branch-info">
                                                        <span className="branch-icon">🌿</span>
                                                        <span className="branch-name">{branch.BranchName}</span>
                                                        {branch.JobId && (
                                                            <span className="branch-job-id">
                                                                Job: {branch.JobId}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div
                                                        className="branch-status"
                                                        style={{ backgroundColor: getStatusColor(branch.Status) }}
                                                    >
                                                        {branch.Status}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-branches">
                                            <span>⏳ Waiting for branches to deploy...</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Amplify Error */}
                    {amplifyError && (
                        <div className="error-banner">
                            <span className="error-icon">⚠️</span>
                            <span>Amplify Status Error: {amplifyError}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export { ProvisioningStatus};